import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { ShieldAlert, Flag } from "lucide-react";
import { api, formatApiError, normalizeLangCode } from "../lib/api";

const SAFETY_EMAIL = "voprea93@yahoo.com";
const STATUSES = ["open", "reviewing", "resolved", "dismissed"];
const DATE_LOCALES = { ro: "ro-RO", en: "en-GB", fr: "fr-FR", de: "de-DE", it: "it-IT", es: "es-ES", pt: "pt-PT", "pt-BR": "pt-BR", sr: "sr-Latn-RS" };

/**
 * Lodge-scoped inbox for in-app safety reports sent from the mobile app.
 * Visible to the lodge's responsible officers (web admins); the backend
 * applies strict lodge / child-lodge isolation (_content_scope).
 */
export default function SafetyReportsPage() {
  const { t, i18n } = useTranslation();
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async (status = filter) => {
    setLoading(true);
    try {
      const { data } = await api.get("/safety-reports", { params: status ? { status } : {} });
      setRows(data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(filter); /* eslint-disable-next-line */ }, [filter]);

  const fmt = (s) => {
    try { return new Date(s).toLocaleString(DATE_LOCALES[normalizeLangCode(i18n.language)] || "ro-RO", { dateStyle: "medium", timeStyle: "short" }); }
    catch { return s; }
  };

  const update = async (id, patch) => {
    try {
      const { data } = await api.patch(`/safety-reports/${id}`, patch);
      setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...data } : r)));
      toast.success(t("safetyReports.saved"));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="safety-reports-page">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2" data-testid="safety-reports-title">
          <ShieldAlert className="w-6 h-6 text-amber-500" /> {t("safetyReports.title")}
        </h1>
        <p className="text-sm text-muted-foreground mt-2 max-w-3xl">{t("safetyReports.subtitle")}</p>
        <p className="text-xs text-muted-foreground mt-1" data-testid="safety-reports-escalation">
          {t("safetyReports.escalationHint", { email: SAFETY_EMAIL })}
        </p>
      </div>

      <div className="flex flex-wrap gap-2" data-testid="safety-reports-filters">
        {["", ...STATUSES].map((s) => (
          <button
            key={s || "all"}
            onClick={() => setFilter(s)}
            data-testid={`safety-filter-${s || "all"}`}
            className={`px-3 py-1 rounded-full text-xs border ${filter === s ? "bg-amber-500 text-black border-amber-500" : "border-border hover:bg-muted"}`}
          >
            {s ? t(`safetyReports.status_${s}`) : t("safetyReports.filterAll")}
          </button>
        ))}
      </div>

      {loading ? null : rows.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="safety-reports-empty">{t("safetyReports.empty")}</p>
      ) : (
        <div className="space-y-4">
          {rows.map((r) => <ReportCard key={r.id} r={r} t={t} fmt={fmt} onUpdate={update} />)}
        </div>
      )}
    </div>
  );
}

function ReportCard({ r, t, fmt, onUpdate }) {
  const [note, setNote] = useState(r.admin_note || "");
  const priority = r.category === "child_safety";
  return (
    <div
      className={`rounded-xl border p-4 space-y-3 ${priority ? "border-red-500/60 bg-red-500/5" : "border-border bg-card"}`}
      data-testid={`safety-report-${r.id}`}
    >
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <Flag className={`w-4 h-4 ${priority ? "text-red-500" : "text-amber-500"}`} />
        <span className="font-semibold" data-testid={`safety-report-category-${r.id}`}>{t(`safetyReports.cat_${r.category}`, r.category)}</span>
        {priority && <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-red-500 text-white">{t("safetyReports.priorityBadge")}</span>}
        <span className="text-muted-foreground">· {fmt(r.created_at)}</span>
        <span className="text-muted-foreground">· {t("safetyReports.colReporter")}: {r.reporter_name || "—"}</span>
        <span className="ml-auto text-xs font-mono text-muted-foreground">#{String(r.id).slice(0, 8).toUpperCase()}</span>
      </div>
      <p className="text-sm whitespace-pre-wrap" data-testid={`safety-report-description-${r.id}`}>{r.description}</p>
      {(r.context_type || r.reported_member_name) && (
        <p className="text-xs text-muted-foreground">
          {r.context_type ? `${t("safetyReports.colContext")}: ${r.context_type}${r.context_id ? ` (${r.context_id})` : ""}` : ""}
          {r.reported_member_name ? ` · ${t("safetyReports.reportedMember")}: ${r.reported_member_name}` : ""}
        </p>
      )}
      <div className="flex flex-col md:flex-row gap-3 md:items-end">
        <label className="text-xs flex flex-col gap-1">
          {t("safetyReports.colStatus")}
          <select
            value={r.status}
            onChange={(e) => onUpdate(r.id, { status: e.target.value })}
            className="border rounded-md px-2 py-1 bg-background text-sm"
            data-testid={`safety-report-status-${r.id}`}
          >
            {STATUSES.map((s) => <option key={s} value={s}>{t(`safetyReports.status_${s}`)}</option>)}
          </select>
        </label>
        <label className="text-xs flex-1 flex flex-col gap-1">
          {t("safetyReports.adminNote")}
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={t("safetyReports.adminNotePlaceholder")}
            rows={2}
            className="border rounded-md px-2 py-1 bg-background text-sm"
            data-testid={`safety-report-note-${r.id}`}
          />
        </label>
        <button
          onClick={() => onUpdate(r.id, { admin_note: note })}
          className="px-3 py-1.5 rounded-md bg-amber-500 text-black text-sm font-semibold hover:bg-amber-400"
          data-testid={`safety-report-save-${r.id}`}
        >
          {t("safetyReports.save")}
        </button>
      </div>
      {r.handled_by && <p className="text-[11px] text-muted-foreground">{t("safetyReports.handledBy")}: {r.handled_by}</p>}
    </div>
  );
}
